import React, { useState, useEffect } from "react";
import { Navigate, useLocation, useParams } from "react-router-dom";
import { supabase } from "../../supabaseClient";
import { TournamentRouteProvider } from "../../context/TournamentRouteContext";
import {
  isTournamentUuid,
  rewriteTournamentPath,
} from "../../utility/tournamentUrls";
import SEOHead from "../SEO/SEOHead";
import { TournamentPageSkeleton } from "../UI/DesignStandards";
import NotFound from "../../pages/NotFound/NotFound";
import TournamentNotFound from "../../pages/NotFound/TournamentNotFound";
import {
  prerenderTournamentForPath,
  readPrerenderData,
} from "../../prerender/pageData";
import SquadNotFound from "../../pages/NotFound/SquadNotFound";
import TeamNotFound from "../../pages/NotFound/TeamNotFound";
import InviteNotFound from "../../pages/NotFound/InviteNotFound";

const UUID_REGEX =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

// Entity validation configurations
const ENTITY_CONFIGS = {
  tournament: {
    table: "tournaments",
    idParam: "tournamentId",
    notFoundComponent: TournamentNotFound,
    errorMessage: "Tournament not found",
  },
  squad: {
    table: "squads",
    idParam: "squadId",
    notFoundComponent: SquadNotFound,
    errorMessage: "Squad not found",
    parentEntity: "tournament", // Squad belongs to tournament
  },
  team: {
    table: "teams",
    idParam: "teamId",
    notFoundComponent: TeamNotFound,
    errorMessage: "Team not found",
    parentEntity: "tournament", // Team belongs to tournament
  },
  invite: {
    table: "organizationInvites",
    idParam: "inviteId",
    notFoundComponent: InviteNotFound,
    errorMessage: "Invite not found",
  },
};

const entityKeyFromParams = (entities, params) =>
  (entities || [])
    .map((entityType) => {
      const config = ENTITY_CONFIGS[entityType];
      const id = config ? params?.[config.idParam] || "" : "";
      return `${entityType}:${id}`;
    })
    .join("|");

const resolveTournament = async (param) => {
  if (!param) return null;
  if (isTournamentUuid(param)) {
    const { data, error } = await supabase
      .from("tournaments")
      .select("id, slug")
      .eq("id", param)
      .maybeSingle();
    if (error || !data) return null;
    return data;
  }
  const { data, error } = await supabase
    .from("tournaments")
    .select("id, slug")
    .eq("slug", param)
    .maybeSingle();
  if (!error && data) return data;
  const { data: caseInsensitive, error: caseError } = await supabase
    .from("tournaments")
    .select("id, slug")
    .ilike("slug", param)
    .maybeSingle();
  if (caseError || !caseInsensitive) return null;
  return caseInsensitive;
};

const UUIDValidationWrapper = ({
  children,
  entities = [], // Array of entity types to validate, e.g., ["tournament", "squad"]
  fallbackComponent = NotFound,
}) => {
  const params = useParams();
  const location = useLocation();
  const entityKey = entityKeyFromParams(entities, params);
  const [validationState, setValidationState] = useState(() => {
    const seeded = prerenderTournamentForPath(location.pathname);
    if (!seeded?.tournament?.id) {
      return {
        loading: true,
        valid: false,
        invalidEntity: null,
        error: null,
        tournament: null,
      };
    }
    return {
      loading: false,
      valid: true,
      invalidEntity: null,
      error: null,
      tournament: {
        id: seeded.tournament.id,
        slug: seeded.tournament.slug || seeded.tournament.id,
      },
    };
  });

  useEffect(() => {
    const validateEntities = async () => {
      try {
        setValidationState((current) =>
          current.valid && current.tournament
            ? current
            : {
                loading: true,
                valid: false,
                invalidEntity: null,
                error: null,
                tournament: null,
              }
        );

        let resolvedTournament = null;

        // Validate all required entities
        for (const entityType of entities) {
          const config = ENTITY_CONFIGS[entityType];
          if (!config) {
            console.error(`Unknown entity type: ${entityType}`);
            continue;
          }

          const entityId = params[config.idParam];
          if (!entityId) {
            setValidationState({
              loading: false,
              valid: false,
              invalidEntity: entityType,
              error: `${config.idParam} is required`,
              tournament: null,
            });
            return;
          }

          if (entityType === "tournament") {
            resolvedTournament = await resolveTournament(entityId);
            if (!resolvedTournament) {
              setValidationState({
                loading: false,
                valid: false,
                invalidEntity: entityType,
                error: config.errorMessage,
                tournament: null,
              });
              return;
            }
            continue;
          }

          // Validate UUID format
          if (!UUID_REGEX.test(entityId)) {
            console.warn(`Invalid ${entityType} ID format:`, entityId);
            setValidationState({
              loading: false,
              valid: false,
              invalidEntity: entityType,
              error: `Invalid ${entityType} ID format`,
              tournament: resolvedTournament,
            });
            return;
          }

          // Check if entity exists in database
          let query;
          if (entityType === "invite") {
            // Use the secure function for invite validation
            query = supabase.rpc("get_invite_by_id", { invite_id: entityId });
          } else {
            query = supabase.from(config.table).select("id").eq("id", entityId);
          }

          // If entity has a parent, also validate the parent relationship
          if (config.parentEntity) {
            const parentConfig = ENTITY_CONFIGS[config.parentEntity];
            const parentParam = params[parentConfig.idParam];
            const parentId =
              parentConfig.idParam === "tournamentId" && resolvedTournament
                ? resolvedTournament.id
                : parentParam;

            if (parentId) {
              // For squads, check they belong to the tournament
              if (entityType === "squad") {
                query = query.eq("tournamentID", parentId);
              }
              // For teams, check they belong to the tournament (through squads)
              else if (entityType === "team") {
                // Teams are linked to tournaments through squads, so we need a more complex check
                const { data: teamData, error: teamError } = await supabase
                  .from("teams")
                  .select(
                    `
                    id,
                    squads!inner (
                      tournamentID
                    )
                  `
                  )
                  .eq("id", entityId)
                  .eq("squads.tournamentID", parentId)
                  .single();

                if (teamError || !teamData) {
                  setValidationState({
                    loading: false,
                    valid: false,
                    invalidEntity: entityType,
                    error: config.errorMessage,
                    tournament: resolvedTournament,
                  });
                  return;
                }
                continue; // Skip the regular query for teams
              }
            }
          }

          let result;
          if (entityType === "invite") {
            // RPC call doesn't need .single()
            result = await query;
          } else {
            // Direct table query needs .single()
            result = await query.single();
          }

          const { data, error } = result;

          if (error || !data) {
            setValidationState({
              loading: false,
              valid: false,
              invalidEntity: entityType,
              error: config.errorMessage,
              tournament: resolvedTournament,
            });
            return;
          }

          // For invite RPC call, check if we got any results
          if (entityType === "invite" && (!data || data.length === 0)) {
            setValidationState({
              loading: false,
              valid: false,
              invalidEntity: entityType,
              error: config.errorMessage,
              tournament: resolvedTournament,
            });
            return;
          }
        }

        // All entities are valid
        setValidationState({
          loading: false,
          valid: true,
          invalidEntity: null,
          error: null,
          tournament: resolvedTournament,
        });
      } catch (err) {
        console.error("Error validating entities:", err);
        setValidationState({
          loading: false,
          valid: false,
          invalidEntity: null,
          error: "Validation error occurred",
          tournament: null,
        });
      }
    };

    validateEntities();
    // Validate when the path IDs change, not when React Router gives a new
    // params object. Search params (like squad on Lanes) must not remount.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- entityKey
  }, [entityKey]);

  const prerenderData = readPrerenderData();
  if (prerenderData?.notFound && prerenderData.path === location.pathname) {
    return <NotFound />;
  }

  const tournamentParam = params.tournamentId;
  const uuidRequest = isTournamentUuid(tournamentParam);

  // Show loading while validating
  if (validationState.loading) {
    return (
      <>
        {uuidRequest ? (
          <SEOHead
            title="Tournament"
            description="Tournament page."
            noIndex
          />
        ) : null}
        <div className="bg-brand-cream min-h-[50vh] p-3">
          <div className="max-w-6xl mx-auto">
            <TournamentPageSkeleton />
          </div>
        </div>
      </>
    );
  }

  // Show appropriate error page if validation failed
  if (!validationState.valid && validationState.invalidEntity) {
    const config = ENTITY_CONFIGS[validationState.invalidEntity];
    const NotFoundComponent = config?.notFoundComponent || fallbackComponent;
    return <NotFoundComponent />;
  }

  // Show generic error if validation failed for unknown reason
  if (!validationState.valid) {
    return <fallbackComponent />;
  }

  const resolved = validationState.tournament;
  if (
    resolved?.slug &&
    tournamentParam &&
    tournamentParam !== resolved.slug
  ) {
    const nextPath = rewriteTournamentPath(
      location.pathname,
      tournamentParam,
      resolved.slug
    );
    return (
      <>
        <SEOHead
          title="Tournament"
          description="Tournament page."
          canonicalUrl={nextPath}
          noIndex={uuidRequest}
        />
        <Navigate
          to={`${nextPath}${location.search}${location.hash}`}
          replace
        />
      </>
    );
  }

  if (resolved?.id) {
    return (
      <TournamentRouteProvider
        tournamentId={resolved.id}
        slug={resolved.slug || resolved.id}
      >
        {children}
      </TournamentRouteProvider>
    );
  }

  return children;
};

export default UUIDValidationWrapper;
